import { useNavigate } from "react-router";
import { confirmDeleteCommunity } from "../../components/confirmations";
import { useQueryClient } from "@tanstack/react-query";
import type { CommunityStructure } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { useIsDesktop } from "../../lib/hooks";
import { CommunityIcon } from "../../components/ui/Avatar";
import { IconButton } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/Controls";
import { SettingsLayout, type SettingsNavGroup } from "../../components/ui/Settings";
import { toast } from "../../components/ui/Toast";
import {
  IconBack,
  IconBlock,
  IconBrush,
  IconHash,
  IconHistory,
  IconIdCard,
  IconLink,
  IconLock,
  IconShield,
  IconShieldCheck,
  IconTrash,
  IconUsers,
  IconWidget,
  IconHandshake,
} from "../../components/icons";
import { OnboardingSection } from "./settings/OnboardingSection";
import { permsOf } from "./hooks";
import { ProfileSection } from "./settings/ProfileSection";
import { AccessSection, AppearanceSection, SafetySection } from "./settings/GeneralSections";
import { RolesSection } from "./settings/RolesSection";
import { AuditLogSection, BansSection, InvitesSection, MembersSection } from "./settings/PeopleSections";
import { ChannelsSection } from "./settings/ChannelsSection";
import { IntegrationsSection } from "./settings/IntegrationsSection";

type Section = "profile" | "appearance" | "access" | "onboarding" | "channels" | "integrations" | "roles" | "members" | "invites" | "safety" | "audit" | "bans" | "delete";

const TITLES: Record<Section, string> = {
  profile: "Community profile",
  appearance: "Appearance",
  access: "Access",
  onboarding: "Onboarding",
  channels: "Channels",
  integrations: "Integrations",
  roles: "Roles",
  members: "Members",
  invites: "Invites",
  safety: "Safety",
  audit: "Audit log",
  bans: "Bans",
  delete: "Delete community",
};

export function CommunitySettings({ structure, section: sectionParam, sub }: { structure: CommunityStructure; section?: string; sub?: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const desktop = useIsDesktop();
  const c = structure.community;
  const perms = permsOf(c.myPermissions, c.isOwner);
  const base = `/c/${c.id}/settings`;

  const groups: SettingsNavGroup<Section>[] = [
    {
      title: "Community",
      items: [
        perms.manageCommunity && { id: "profile" as const, label: "Profile", icon: IconIdCard, keywords: "name icon banner description topics traits" },
        perms.manageCommunity && { id: "appearance" as const, label: "Appearance", icon: IconBrush, keywords: "accent colour theme banner landing channel system messages welcome join" },
        perms.manageCommunity && { id: "access" as const, label: "Access", icon: IconLock, keywords: "visibility public private rules ownership transfer feed" },
        perms.manageCommunity && { id: "onboarding" as const, label: "Onboarding", icon: IconHandshake, keywords: "welcome rules questions roles lobby intro new members" },
        (perms.manageChannels || perms.manageRoles) && { id: "channels" as const, label: "Channels", icon: IconHash, keywords: "permissions overrides slow mode user limit voice private" },
        perms.manageCommunity && { id: "integrations" as const, label: "Integrations", icon: IconWidget, keywords: "apps bots commands slash developer" },
      ].filter(Boolean) as SettingsNavGroup<Section>["items"],
    },
    {
      title: "People",
      items: [
        (perms.manageRoles || perms.kick || perms.ban || perms.timeout) && { id: "members" as const, label: "Members", icon: IconUsers, keywords: "kick ban timeout roles" },
        perms.manageRoles && { id: "roles" as const, label: "Roles", icon: IconShield, keywords: "permissions hierarchy colours presets moderator manager" },
        (perms.manageCommunity || perms.manageInvites) && { id: "invites" as const, label: "Invites", icon: IconLink, keywords: "links codes" },
      ].filter(Boolean) as SettingsNavGroup<Section>["items"],
    },
    {
      title: "Moderation",
      items: [
        perms.manageCommunity && { id: "safety" as const, label: "Safety", icon: IconShieldCheck, keywords: "blocked words links account age spam filter" },
        perms.auditLog && { id: "audit" as const, label: "Audit log", icon: IconHistory, keywords: "history changes" },
        perms.ban && { id: "bans" as const, label: "Bans", icon: IconBlock },
      ].filter(Boolean) as SettingsNavGroup<Section>["items"],
    },
  ].filter((g) => g.items.length);

  const all = groups.flatMap((g) => g.items.map((i) => i.id));
  const section = (all.includes(sectionParam as Section) ? (sectionParam as Section) : desktop ? all[0] : undefined) as Section | undefined;
  const go = (s: string, subId?: string) => navigate(`${base}/${s}${subId ? `/${subId}` : ""}`, { replace: desktop });

  const remove = async () => {
    const ok = await confirmDeleteCommunity(c.name);
    if (!ok) return;
    try {
      await api.del(`/api/communities/${c.id}`);
      await qc.invalidateQueries({ queryKey: ["my-communities"] });
      navigate("/c", { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (!all.length) return <EmptyState title="Nothing to manage" description="You don't have permission to change this community's settings." />;

  const props = { structure, go, sub };

  return (
    <SettingsLayout<Section>
      title={c.name}
      header={
        <div className="flex h-10 items-center gap-2">
          <IconButton label="Back to community" icon={IconBack} onClick={() => navigate(`/c/${c.id}`)} className="-ml-1" />
          <CommunityIcon src={c.iconUrl} name={c.name} size="xs" />
          <span className="truncate text-sm font-semibold">{c.name}</span>
        </div>
      }
      groups={groups}
      active={section}
      onSelect={(s) => go(s)}
      onBack={() => navigate(base)}
      sectionTitle={section ? TITLES[section] : undefined}
      wide={section === "roles" || section === "channels" || section === "profile" || section === "appearance"}
      footer={
        c.isOwner ? (
          <button onClick={() => void remove()} className="flex h-9 w-full items-center gap-2.5 rounded-md px-2 text-sm text-danger hover:bg-danger-soft">
            <IconTrash className="icon size-[18px]" aria-hidden />
            <span className="flex-1 text-left">Delete community</span>
          </button>
        ) : undefined
      }
    >
      {section === "profile" && <ProfileSection {...props} />}
      {section === "appearance" && <AppearanceSection {...props} />}
      {section === "access" && <AccessSection {...props} />}
      {section === "onboarding" && <OnboardingSection {...props} />}
      {section === "channels" && <ChannelsSection {...props} />}
      {section === "integrations" && <IntegrationsSection {...props} />}
      {section === "roles" && <RolesSection {...props} />}
      {section === "members" && <MembersSection {...props} />}
      {section === "invites" && <InvitesSection {...props} />}
      {section === "safety" && <SafetySection {...props} />}
      {section === "audit" && <AuditLogSection {...props} />}
      {section === "bans" && <BansSection {...props} />}
    </SettingsLayout>
  );
}
